import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import APIEndpoints from 'src/Admin/api/APIEndpoints';
import { HttpSuccessCodes } from 'src/Admin/errorhandling/HttpErrorCodes';
import { UpdateNetworkConfig } from 'src/Admin/utils/common';
import { axiosInstance } from 'src/api/axios';
import { GetCustomNetworkList } from 'src/redux/app/api-calls/configCalls';
import ToastMessage from '../../common/ToastMessage';
import GenericSkeleton from '../../common/skeleton/GenericSkeleton';
import GenericButton from '../../../../Components/GenericButton/GenericButton';
import WarningComponent from '../WarningComponent';
import './configuration-table.scss';
import Checkbox from '../../common/Checkbox';
import { prioritizeArrayElement } from 'src/common/utils';

function ConfigurationTable() {
  const dispatch = useDispatch();
  const { Coverage_Network, Find_Provider_Network } = useSelector(
    (state) => state.app?.customNetwork,
  );

  const [rows, setRows] = useState([]);
  const defaultItem = {
    Default: false,
    Display_On_Portal: false,
    NetworkID: 'Default',
    NetworkName: 'Default',
    Portal_Display_Label: 'Default',
  };
  const [oldRowsData, setOldRowsData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [valuesChanged, setValueChanged] = useState(false);
  const [coverageNetworkTabSelected, setCoverageNetworkTabSelected] =
    useState(true);

  const [loader, setLoader] = useState(false);

  const updateNetwork = async (
    coverageNetworkData,
    findProviderNetworkData,
  ) => {
    coverageNetworkData = coverageNetworkData.filter(
      (item) => item?.Display_On_Portal === true || item?.Default === true,
    );
    findProviderNetworkData = findProviderNetworkData.filter(
      (item) => item?.Display_On_Portal === true || item?.Default === true,
    );
    const saveJson = UpdateNetworkConfig({
      Coverage_Network: coverageNetworkData,
      Find_Provider_Network: findProviderNetworkData,
    });
    return await axiosInstance.put(
      APIEndpoints.putUpdateConfiguration(),
      JSON.stringify(saveJson),
    );
  };

  const updateDataApiCallHandler = async () => {
    const updatedFilteredData = rows.filter(
      (item) => item?.Display_On_Portal === true || item?.Default === true,
    );
    const coverageNetworkData =
      coverageNetworkTabSelected === true ? updatedFilteredData : oldRowsData;
    const findProviderNetworkData =
      coverageNetworkTabSelected === false ? updatedFilteredData : oldRowsData;
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    try {
      const updateNetworkResponce = await updateNetwork(
        coverageNetworkData,
        findProviderNetworkData,
      );
      if (
        HttpSuccessCodes.HTTP_SUCCESS_CODE_200 === updateNetworkResponce.status
      ) {
        dispatch(GetCustomNetworkList());
      }
      toast.success(
        <ToastMessage
          type="success"
          subTitle={'Network Configuration updated successfully'}
        />,
        setEditMode(false),
        setValueChanged(false),
      );
    } catch (error) {
      console.log(error);
      if (error.toString().includes('409')) {
        toast.warning(
          <ToastMessage
            type="warning"
            subTitle={'Could not update Network Configuration.'}
          />,
        );
      } else {
        toast.error(
          <ToastMessage
            type="error"
            subTitle={'Network Configuration updation failed'}
          />,
        );
      }
    }
  };

  const updateRowValuesHandler = (value, id, propertyName) => {
    let rowsData = rows.map((item) => item);
    rowsData = rowsData.map((item) => ({
      Default: false,
      Display_On_Portal: false,
      ...item,
      Portal_Display_Label: item.Portal_Display_Label || item.NetworkName,
    }));

    const selectedRow = rowsData.filter((item) => item?.NetworkID === id)[0];
    const selectedRowObj = { ...selectedRow };

    switch (propertyName) {
      case 'Portal_Display_Label':
        selectedRowObj.Portal_Display_Label = value;
        setValueChanged(true);
        break;
      case 'Display_On_Portal':
        selectedRowObj.Display_On_Portal = value;
        setValueChanged(true);
        break;
      case 'Default':
        selectedRowObj.Default = value;
        setValueChanged(true);
        break;
      default:
        break;
    }

    rowsData.filter((item) => item.NetworkID === id)[0].Portal_Display_Label =
      selectedRowObj?.Portal_Display_Label;
    rowsData.filter((item) => item.NetworkID === id)[0].Display_On_Portal =
      selectedRowObj?.Display_On_Portal;
    rowsData.filter((item) => item.NetworkID === id)[0].Default =
      selectedRowObj?.Default;

    setRows(rowsData.map((item) => item));
  };

  const renderRow = (row) => {
    return (
      <tr key={row?.NetworkID}>
        <td className="text-left pl-2">{row?.NetworkID}</td>
        <td className="text-left pl-2">{row?.NetworkName}</td>
        <td>
          <input
            type="text"
            id={`Portal_Display_Label-${row?.NetworkID}`}
            name={`Portal_Display_Label-${row?.NetworkID}`}
            className="w-100 border-1 input-field network-configuration-portal-label-display-field"
            onChange={(e) => {
              updateRowValuesHandler(
                e.target.value,
                row?.NetworkID,
                'Portal_Display_Label',
              );
            }}
            defaultValue={row?.Portal_Display_Label || row?.NetworkName}
            disabled={!editMode}
          />
        </td>
        <td>
          <div className="d-flex flex-wrap align-items-center justify-content-center">
            <Checkbox
              inline
              id={`Display_On_Portal-${row?.NetworkID}`}
              name={`Display_On_Portal-${row?.NetworkID}`}
              key={`Display_On_Portal-${row?.NetworkID}`}
              label=""
              className="d-none"
              value={row?.NetworkID}
              onChange={(e) => {
                updateRowValuesHandler(
                  e.target.checked,
                  row?.NetworkID,
                  'Display_On_Portal',
                );
              }}
              checked={row?.Display_On_Portal}
              isDisabled={!editMode}
            />
            {/*<label className="my-auto" htmlFor={`Display_On_Portal-${row?.NetworkID}`}>YES</label> */}
          </div>
        </td>
        <td>
          <div className="d-flex flex-wrap align-items-center justify-content-center">
            <Checkbox
              inline
              id={`Default-${row?.NetworkID}`}
              name={`Default-${row?.NetworkID}`}
              key={`Default-${row?.NetworkID}`}
              label=""
              className="d-none"
              value={row?.NetworkID}
              onChange={(e) => {
                updateRowValuesHandler(
                  e.target.checked,
                  row?.NetworkID,
                  'Default',
                );
              }}
              checked={row?.Default === true}
              isDisabled={!editMode}
            />
            {/*<label className="my-auto" htmlFor={`Default-${row?.NetworkID}`}>YES</label> */}
          </div>
        </td>
      </tr>
    );
  };

  const tabToggleHandler = (selectedTab) => {
    if (
      !(
        (coverageNetworkTabSelected && selectedTab === 1) ||
        (!coverageNetworkTabSelected && selectedTab === 2)
      )
    ) {
      setCoverageNetworkTabSelected((prevState) => !prevState);
    }
  };

  useEffect(() => {
    setLoader(true);
    setEditMode(false);
    setValueChanged(false);
    if (!(Coverage_Network?.length > 0 || Find_Provider_Network?.length > 0))
      dispatch(GetCustomNetworkList());
    const getResources = async () => {
      return await axiosInstance.get(`${APIEndpoints.getAllNetworkList()}`);
    };
    (async () => {
      try {
        const resourceData = await getResources();
        let resourceList = resourceData?.data?.Response?.networks;
        if (!coverageNetworkTabSelected) {
          const commonNetworks = [
            {
              EffectiveDate: '1900-01-01',
              NetworkID: 'all-include-out-of-network',
              NetworkName: 'All - Including Out of Network Providers',
            },
            {
              EffectiveDate: '1900-01-01',
              NetworkID: 'all-plan-networks',
              NetworkName: 'All Plan Networks',
            },
          ];
          resourceList = [...commonNetworks, ...resourceList];
        }
        resourceList = resourceList.map((item) => ({
          ...item,
          Portal_Display_Label: item?.Portal_Display_Label || '',
          Default: item?.Default || false,
          Display_On_Portal: item?.Display_On_Portal || false,
        }));

        const filterFromArray = [];
        const filteredDataForOtherTab = [];
        const otherTabDefaultItem = [];
        if (coverageNetworkTabSelected) {
          Coverage_Network &&
            Coverage_Network.map((item) => filterFromArray.push(item));
          Find_Provider_Network &&
            Find_Provider_Network.map((item) =>
              filteredDataForOtherTab.push(item),
            );
          if (
            (Find_Provider_Network &&
              Find_Provider_Network[0] &&
              Find_Provider_Network[0]?.NetworkID) === 'Default'
          ) {
            otherTabDefaultItem.push(Find_Provider_Network[0]);
          } else {
            otherTabDefaultItem.push(defaultItem);
          }
        } else {
          Find_Provider_Network &&
            Find_Provider_Network.map((item) => filterFromArray.push(item));
          Coverage_Network &&
            Coverage_Network.map((item) => filteredDataForOtherTab.push(item));
          if (
            (Coverage_Network &&
              Coverage_Network[0] &&
              Coverage_Network[0]?.NetworkID) === 'Default'
          ) {
            otherTabDefaultItem.push(Coverage_Network[0]);
          } else {
            otherTabDefaultItem.push(defaultItem);
          }
        }

        const filteredCommonData = filterFromArray.filter((ar1) =>
          resourceList
            .map((i) => i?.NetworkID)
            .find((ar2) => ar2 === ar1?.NetworkID),
        );
        const defaultItemCommonData =
          (filterFromArray &&
            filterFromArray[0] &&
            filterFromArray[0]?.NetworkID) === 'Default'
            ? filterFromArray[0]
            : defaultItem;
        const filteredDifferentData = resourceList.filter(
          (ar1) =>
            !filterFromArray
              .map((i) => i?.NetworkID)
              .find((ar2) => ar2 === ar1?.NetworkID),
        );
        let finallyFilteredData = [
          defaultItemCommonData,
          ...filteredCommonData,
          ...filteredDifferentData,
        ];

        const referenceArray = [
          'Default',
          'all-plan-networks',
          'all-include-out-of-network',
        ];
        finallyFilteredData = prioritizeArrayElement(
          finallyFilteredData,
          referenceArray,
          'NetworkID',
        );

        setRows(finallyFilteredData);

        const filteredCommonDataForOtherTab = filteredDataForOtherTab.filter(
          (ar1) =>
            resourceList
              .map((i) => i?.NetworkID)
              .find((ar2) => ar2 === ar1?.NetworkID),
        );
        filteredCommonDataForOtherTab.unshift(...otherTabDefaultItem);
        setOldRowsData([...filteredCommonDataForOtherTab]);
      } catch (error) {
        console.error(error);
      } finally {
        setLoader(false);
      }
    })();
  }, [coverageNetworkTabSelected]);

  return (
    <div>
      <div className="parent-div">
        <div>
          <h2>Network Configuration</h2>
        </div>
        <div>
          {rows.length > 0 && (
            <GenericButton
              onClick={() => setEditMode((prevState) => !prevState)}
              className="ml-1"
              disable={editMode}
              buttonDisabled={true}
            >
              {/*{editMode ? 'Disable Editing' : 'Enable Editing'} */}
              Edit
            </GenericButton>
          )}
        </div>
      </div>
      {editMode && (
        <div>
          <WarningComponent />
        </div>
      )}
      <div className="d-flex flex-wrap mb-3 mt-3 tab-container">
        <button
          className={`table-tab-panel first-tab-option ${
            coverageNetworkTabSelected && 'table-tab-selected'
          }`}
          onClick={() => tabToggleHandler(1)}
        >
          Coverage Network
        </button>
        <button
          className={`table-tab-panel ${
            !coverageNetworkTabSelected && 'table-tab-selected'
          }`}
          onClick={() => tabToggleHandler(2)}
        >
          Find Provider Network
        </button>
      </div>
      {/*as of now we are using existing skeleton, we need to create one skeleton for this table */}
      {loader ? (
        <GenericSkeleton />
      ) : (
        <div className="d-flex flex-column justify-content-center w-100">
          <table border={1} className="network-configuration-table">
            <thead>
              <tr>
                <th className="pl-2">JV Network ID</th>
                <th className="pl-2">JV Network Name</th>
                <th>Portal Display Label</th>
                <th>Display on Portal</th>
                <th>Default</th>
              </tr>
            </thead>
            {!loader && (
              <tbody>
                {rows.map((row) => {
                  return renderRow(row);
                })}
              </tbody>
            )}
          </table>
          {editMode && (
            <div className="d-flex flex-wrap justify-content-end mt-5">
              <GenericButton
                className="mr-3"
                onClick={() => {
                  setEditMode(false);
                  setValueChanged(false);
                  window.scrollTo(0, 0);
                }}
                buttonDisabled={!editMode}
                disable={!editMode}
                variant="link"
              >
                Cancel
              </GenericButton>
              <GenericButton
                className="ml-1"
                disable={!valuesChanged || !editMode}
                onClick={() => updateDataApiCallHandler()}
                buttonDisabled={!valuesChanged || !editMode}
              >
                Save
              </GenericButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConfigurationTable;
